import {
  CurtailmentData,
  ReadingsDataProcessedResponse,
} from "@inrange/building-manager-api-client";
import { SiteAllowStringValues } from "@inrange/building-manager-api-client/models-site";
import { useMemo } from "react";
import { Col, Row, Table } from "react-bootstrap";
import { Formatters } from "./types";

const RecentDataTableProcessed = ({
  site,
  data,
  curtailmentData,
  selectedEsysUuid,
  startDate,
  endDate,
  isImportOnly,
  formatters,
}: {
  site: SiteAllowStringValues;
  data: ReadingsDataProcessedResponse;
  curtailmentData?: CurtailmentData;
  selectedEsysUuid: string | undefined;
  startDate: string;
  endDate: string;
  isImportOnly: boolean;
  formatters: Formatters;
}) => {
  const hasBattery =
    site?.totalBatterySummary !== undefined &&
    site?.totalBatterySummary !== null;

  // Get recent data (up to 7 days) for detailed table view
  const recentDataTable = useMemo(() => {
    const allData = data.data;

    // Filter by esysUuid and date range if set
    // Pre-calculate timestamp bounds for efficient filtering
    const { startMs, endMs } =
      startDate && endDate
        ? formatters.dateRangeToTimestamps(startDate, endDate)
        : { startMs: 0, endMs: Infinity };

    let filteredData = allData.filter((point) => {
      // Filter by esysUuid
      if (selectedEsysUuid && point.esysUuid !== selectedEsysUuid) return false;

      if (startDate && endDate) {
        const pointMs = new Date(point.x).getTime();
        if (pointMs < startMs || pointMs > endMs) return false;
      }
      return true;
    });

    // If no specific esysUuid is selected (whole site), aggregate data by timestamp
    if (!selectedEsysUuid && filteredData.length > 0) {
      const aggregatedMap = new Map();

      filteredData.forEach((point) => {
        const key = point.x;
        if (!aggregatedMap.has(key)) {
          aggregatedMap.set(key, {
            x: point.x,
            esysUuid: "aggregated",
            demand: 0,
            generation: 0,
            behindMeterPV: 0,
            import: 0,
            export: 0,
            behindMeterBattery: 0,
            batteryFinalCharge: 0,
            networkImport: 0,
            generationSource: new Set<string>(),
            importSource: new Set<string>(),
            exportSource: new Set<string>(),
            batteryFinalChargeSource: new Set<string>(),
          });
        }

        const agg = aggregatedMap.get(key);
        agg.demand += point.demand;
        agg.generation += point.generation;
        agg.behindMeterPV += point.behindMeterPV;
        agg.import += point.import;
        agg.export += point.export;
        agg.behindMeterBattery += point.behindMeterBattery;
        agg.batteryFinalCharge += point.batteryFinalCharge;
        agg.networkImport += point.networkImport ?? 0;

        // Collect all unique sources
        if (point.generationSource)
          agg.generationSource.add(point.generationSource);
        if (point.importSource) agg.importSource.add(point.importSource);
        if (point.exportSource) agg.exportSource.add(point.exportSource);
        if (point.batteryFinalChargeSource)
          agg.batteryFinalChargeSource.add(point.batteryFinalChargeSource);
      });

      // Convert sets to comma-separated strings and create final array
      filteredData = Array.from(aggregatedMap.values())
        .map((agg) => ({
          ...agg,
          generationSource:
            agg.generationSource.size > 0
              ? Array.from(agg.generationSource).join(", ")
              : undefined,
          importSource:
            agg.importSource.size > 0
              ? Array.from(agg.importSource).join(", ")
              : undefined,
          exportSource:
            agg.exportSource.size > 0
              ? Array.from(agg.exportSource).join(", ")
              : undefined,
          batteryFinalChargeSource:
            agg.batteryFinalChargeSource.size > 0
              ? Array.from(agg.batteryFinalChargeSource).join(", ")
              : undefined,
        }))
        .sort((a, b) => new Date(a.x).getTime() - new Date(b.x).getTime());
    }

    if (filteredData.length === 0) return [];

    // Find the most recent date
    const sortedByDate = [...filteredData].sort(
      (a, b) => new Date(b.x).getTime() - new Date(a.x).getTime()
    );
    const mostRecentDate = new Date(sortedByDate[0].x);

    // Calculate 7 days before the most recent date
    const sevenDaysAgo = new Date(mostRecentDate);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6); // 7 days inclusive

    // Filter to only include last 7 days and sort oldest to newest
    return sortedByDate
      .filter((point) => {
        const pointDate = new Date(point.x);
        return pointDate >= sevenDaysAgo;
      })
      .sort((a, b) => new Date(a.x).getTime() - new Date(b.x).getTime());
  }, [data.data, selectedEsysUuid, startDate, endDate, formatters]);

  const hasNetworkImport = recentDataTable.some(
    (point: any) => (point.networkImport ?? 0) > 0
  );

  // Build curtailment lookup by timestamp for the table
  const curtailmentByTs = useMemo(() => {
    const map = new Map<string, { isCurtailed: boolean; source: string; estKwh: number | null; estSource: string }>();
    if (!curtailmentData) return map;
    for (let i = 0; i < curtailmentData.x.length; i++) {
      map.set(curtailmentData.x[i], {
        isCurtailed: curtailmentData.isCurtailed[i],
        source: curtailmentData.isCurtailedSource[i],
        estKwh: curtailmentData.estimatedCurtailedKwh[i],
        estSource: curtailmentData.estimatedCurtailedKwhSource[i],
      });
    }
    return map;
  }, [curtailmentData]);

  const hasCurtailment = curtailmentByTs.size > 0 && Array.from(curtailmentByTs.values()).some((c) => c.isCurtailed);

  return (
    <>
      {recentDataTable.length > 0 && (
        <Row className="mt-4">
          <Col>
            <h5>Recent Data (Last 7 Days)</h5>
            <p className="text-muted">
              Showing {recentDataTable.length.toLocaleString()} data point
              {recentDataTable.length !== 1 ? "s" : ""} from the most recent 7
              days in the selected range
            </p>
            <div>
              <Table striped bordered hover size="sm" responsive>
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    {isImportOnly ? (
                      <th>Network Import</th>
                    ) : (
                      <>
                        <th>Generation</th>
                        <th>Import</th>
                        {hasNetworkImport && <th>Network Import</th>}
                        <th>Export</th>
                        {hasBattery && (
                          <>
                            <th>Behind Meter Battery</th>
                            <th>Battery Final Charge</th>
                          </>
                        )}
                        {hasCurtailment && (
                          <>
                            <th>Curtailed</th>
                            <th>Est. Lost kWh</th>
                          </>
                        )}
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {recentDataTable.map((point: any, index: number) => (
                    <tr key={index}>
                      <td>{formatters.toLocaleString(point.x)}</td>
                      {isImportOnly ? (
                        <td>{point.networkImport?.toFixed(3) || "—"}</td>
                      ) : (
                        <>
                          <td>
                            {point.generation?.toFixed(3) || "—"}
                            {point.generationSource && (
                              <span className="text-muted">
                                {" "}
                                ({point.generationSource})
                              </span>
                            )}
                          </td>
                          <td>
                            {point.import?.toFixed(3) || "—"}
                            {point.importSource && (
                              <span className="text-muted">
                                {" "}
                                ({point.importSource})
                              </span>
                            )}
                          </td>
                          {hasNetworkImport && (
                            <td>{point.networkImport?.toFixed(3) || "—"}</td>
                          )}
                          <td>
                            {point.export?.toFixed(3) || "—"}
                            {point.exportSource && (
                              <span className="text-muted">
                                {" "}
                                ({point.exportSource})
                              </span>
                            )}
                          </td>
                          {hasBattery && (
                            <>
                              <td>
                                {point.behindMeterBattery?.toFixed(3) || "—"}
                              </td>
                              <td>
                                {point.batteryFinalCharge?.toFixed(3) || "—"}
                                {point.batteryFinalChargeSource && (
                                  <span className="text-muted">
                                    {" "}
                                    ({point.batteryFinalChargeSource})
                                  </span>
                                )}
                              </td>
                            </>
                          )}
                          {hasCurtailment && (() => {
                            const c = curtailmentByTs.get(point.x);
                            return (
                              <>
                                <td>
                                  {c?.isCurtailed ? "Yes" : "—"}
                                  {c?.isCurtailed && c.source && (
                                    <span className="text-muted"> ({c.source})</span>
                                  )}
                                </td>
                                <td>
                                  {c?.estKwh != null ? c.estKwh.toFixed(3) : "—"}
                                  {c?.estKwh != null && c.estSource && (
                                    <span className="text-muted"> ({c.estSource})</span>
                                  )}
                                </td>
                              </>
                            );
                          })()}
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Col>
        </Row>
      )}
    </>
  );
};

export default RecentDataTableProcessed;
